// @owner ClientPlatform

import React from 'react';
import {styled} from 'styled-components';
import {textStyleMixin} from './text';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary';

const StyledButton = styled.button`
  border-radius: 8px;
  height: 38px;

  ${textStyleMixin({$variant: 'subtitle-small-bold'})};

  display: flex;
  align-items: center;

  padding: 0 12px;
  text-decoration: none;
  transition: background-color 100ms ease;

  &[data-variant='primary'] {
    background: var(--color-bg-fill-primary);
    color: var(--color-text-on-bg-fill);

    &:hover {
      background: var(--color-bg-fill-primary-hover);
    }

    &:active {
      background: var(--color-bg-fill-primary-pressed);
    }

    &:disabled {
      background: var(--color-bg-fill-primary-disabled);
      color: var(--color-text-on-bg-fill-disabled);

      &:hover {
        background: var(--color-bg-fill-primary-disabled);
      }
    }
  }

  &[data-variant='secondary'] {
    ${textStyleMixin({$variant: 'body-medium'})};

    background: var(--color-bg-fill-secondary);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-secondary);

    &:hover {
      background: var(--color-bg-fill-secondary-hover);
    }

    &:active {
      background: var(--color-bg-fill-secondary-pressed);
    }

    &:disabled {
      background: var(--color-bg-fill-secondary-disabled);
      color: var(--color-text-disabled);
      border-color: var(--color-border-disabled-secondary);

      &:hover {
        background: var(--color-bg-fill-secondary-disabled);
      }
    }
  }

  &[data-variant='tertiary'] {
    background: transparent;
    color: var(--color-text-interactive);

    &:hover {
      color: var(--color-text-interactive-hover);
    }
  }
`;

const StyledContent = styled.span`
  flex: 1;
  text-align: center;
`;

interface CommonProps {
  className?: string;
  id?: string;
  variant?: ButtonVariant;
  testId?: string;
  leadingAccessory?: React.ReactNode;
  children: React.ReactNode;
}

interface ButtonAsButtonProps extends CommonProps {
  type: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

interface ButtonAsLinkProps extends CommonProps {
  href: string;
  target?: string;
  rel?: string;
}

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

export const Button: React.FC<ButtonProps> = (props) => {
  const {className, id, variant = 'primary', testId, leadingAccessory, children} = props;
  const content = (
    <>
      {leadingAccessory}
      <StyledContent>{children}</StyledContent>
    </>
  );

  // Navigation renders a real anchor (styled via `as`), never a <button> nested in
  // an <a>; actions render a <button>. The element follows the semantics.
  if ('href' in props) {
    return (
      <StyledButton
        as="a"
        href={props.href}
        target={props.target}
        rel={props.rel}
        className={className}
        id={id}
        data-variant={variant}
        data-testid={testId}
      >
        {content}
      </StyledButton>
    );
  }

  return (
    <StyledButton
      type={props.type}
      disabled={props.disabled}
      onClick={props.onClick}
      className={className}
      id={id}
      data-variant={variant}
      data-testid={testId}
    >
      {content}
    </StyledButton>
  );
};

export const FullWidthButton = styled(Button)`
  width: 100%;
`;
